import React, { useEffect } from 'react'
import { BrowserRouter as Router, Switch, Redirect, Route } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import WindowSizeListener from 'react-window-size-listener'
import { ToastContainer } from 'react-toastify'
import smoothscroll from 'smoothscroll-polyfill'
import * as Sentry from '@sentry/react'

import MainLayout from 'layout/MainLayout'

import oauth from 'pages/oauthLogin'
import ErrorPage from 'pages/Error'
import { DialogProvider } from 'components/Modal/NotificationDialog/DialogContext'
import CookiesBanner from 'components/CookiesBanner'
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary'
import Loader from 'components/Loader'
import SEO from 'components/SEO'

import useFacebookChat from 'hooks/useFacebookChat'
import useGoogleTagManager from 'hooks/useGoogleTagManager'
import useGoogleAnalytics, { GoogleAnalyticsTracker } from 'hooks/useGoogleAnalytics'

import { SERVICE__STYLES__SET_WINDOW_SIZES, COMMON__SET_LANGUAGE, COMMON__SET_APP_LOADED } from 'store/actions'

import ScrollToTop from 'routes/utils/ScrollToTop'
import CustomRoute from 'routes/CustomRoute'
import AuthRoutes from 'routes/Auth'

import './styles/reset.scss'
import 'react-toastify/dist/ReactToastify.min.css'
import './styles/main.scss'
import 'animate.css'
import 'simplebar/dist/simplebar.min.css'

smoothscroll.polyfill()

const App = ({ history }) => {
    const isAppLoaded = useSelector(state => state.app_loaded)
    const dispatch = useDispatch()
    const {
        i18n: { language },
    } = useTranslation()

    useFacebookChat(isAppLoaded)
    useGoogleTagManager()
    useGoogleAnalytics(isAppLoaded)

    useEffect(() => {
        dispatch(COMMON__SET_LANGUAGE({ code: language, isForceSetCookie: false }))
        dispatch(COMMON__SET_APP_LOADED(true))
    }, [])

    const onResizeWindow = data => {
        dispatch(
            SERVICE__STYLES__SET_WINDOW_SIZES({
                width: data.windowWidth,
                height: data.windowHeight,
            }),
        )
    }

    if (!isAppLoaded) return <Loader text="App loading..." />

    return (
        <ErrorBoundary isShowReloadBtn>
            <DialogProvider>
                <div className="wrapper">
                    <SEO
                        title="Create interactive content in minutes. Free"
                        addTitleLabel={false}
                        description="One of the most powerful editors for interactive content. Create quizzes, photogalleries and games. Generate leads, collect data and drive more traffic. Start with a template. Customize like never before. Get maximum efficiency with detailed analytics and integrations."
                        thumbnail="/sharing.png"
                        thumbnailType="image/png"
                        thumbnailWidth="1000"
                        thumbnailHeight="672"
                        locale={language}
                    />

                    <WindowSizeListener onResize={onResizeWindow} />
                    <Router history={history}>
                        <GoogleAnalyticsTracker />

                        <ScrollToTop />

                        <Switch>
                            <Route exact path={['/auth']} component={AuthRoutes} />
                            <Route exact path={['/error']} component={ErrorPage} />
                            <Route exact path={['/oauth2/redirect']} component={oauth} />
                            <CustomRoute component={MainLayout} />
                            <Redirect from="*" to="/my-projects" />
                        </Switch>
                    </Router>
                    <ToastContainer />
                    <div
                        id="editor-tip__fb-mock"
                        style={{
                            position: 'absolute',
                            right: '24px',
                            bottom: '24px',
                            width: '60px',
                            height: '60px',
                        }}
                    />
                    <CookiesBanner />
                </div>
            </DialogProvider>
        </ErrorBoundary>
    )
}

export default Sentry.withProfiler(App)
